import React from 'react';
import { cn } from '../../utils/cn';

import { useLayoutVariant } from '../../hooks/useLayoutVariant';

interface MobileGameLayoutProps {
  header?: React.ReactNode;
  mainContent: React.ReactNode;
  hints: React.ReactNode;
  controls: React.ReactNode;
  className?: string;
}

/**
 * MobileGameLayout component for mobile-specific stacking
 * 
 * Provides mobile-optimized layout with proper spacing, padding, and touch-friendly
 * interaction areas. Implements vertical stacking optimized for small screens.
 * 
 * Requirements addressed:
 * - 1.1: Mobile layout optimized for touch interaction and small screens
 * - 1.5: Touch-friendly interaction areas and appropriately sized controls
 */
export const MobileGameLayout: React.FC<MobileGameLayoutProps> = ({
  header,
  mainContent,
  hints,
  controls,
  className
}) => {
  const { isStreamerMode, isActuallyDesktop } = useLayoutVariant();
  const showOnDesktop = isStreamerMode && isActuallyDesktop;
  const compact = isStreamerMode;

  return (
    <div 
      className={cn(
        // Hide on large screens unless streamer mode forces mobile layout on desktop
        !showOnDesktop && "lg:hidden",
        // Mobile-specific stacking layout
        "mobile-game-layout",
        // Vertical flex layout with proper spacing
        "flex flex-col",
        // Mobile-optimized spacing and padding
        compact ? "space-y-1" : "space-y-4 sm:space-y-6",
        // Full width with minimal padding
        "w-full",
        // Minimum height to fill available space
        "min-h-[calc(100vh-var(--header-height,4rem))]",
        // Minimal horizontal padding for mobile; tighter in streamer mode
        compact ? "px-2 py-1" : "px-2 py-4 sm:px-4 sm:py-6",
        className
      )}
      role="main"
      aria-label="Mobile game layout"
    >
      {/* Optional Header Section */}
      {header && (
        <div className="mobile-header-section">
          {header}
        </div>
      )}

      {/* Main Content Section */}
      <div className={cn(
        "mobile-main-content",
        // Flexible growth to take available space
        "flex-1",
        // Mobile-optimized spacing
        compact ? "space-y-1" : "space-y-4 sm:space-y-6"
      )}>
        {mainContent}
      </div>

      {/* Hints Section */}
      <div
        data-tutorial-id="hints-section"
        className={cn(
          "mobile-hints-section",
          compact ? "py-0 space-y-0" : "py-2 space-y-2"
        )}
      >
        {hints}
      </div>

      {/* Controls Section */}
      <div
        data-tutorial-id="submit-guesses"
        className={cn(
          "mobile-controls-section",
          // Background for guess input area
          "bg-[hsl(var(--dashboard-main-background))] rounded-xl mx-[-0.5rem] px-2",
          // Touch-friendly padding
          compact ? "py-1 space-y-2" : "py-4 space-y-3"
        )}
      >
        {controls}
      </div>
    </div>
  );
};

export default MobileGameLayout;
